import { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiUser, FiPlus, FiEye, FiEyeOff, FiLock, FiMove } from 'react-icons/fi';
import { cpApi } from '../api/api';

function getApiError(err: unknown): string {
  if (typeof err === 'string') return err;
  const e = err as { response?: { data?: string | { error?: string } }; message?: string };
  const data = e?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') return (data as { error?: string }).error || 'Ошибка';
  return e?.message || 'Ошибка';
}

interface Props {
  teamName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddTeamMemberWidget({ teamName, isOpen, onClose, onSuccess }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 24, y: 90 });
  const [isPinned, setIsPinned] = useState(true);
  const dragRef = useRef<{ isDragging: boolean; offsetX: number; offsetY: number }>({
    isDragging: false,
    offsetX: 0,
    offsetY: 0,
  });
  const hasMovedRef = useRef(false);

  const [formData, setFormData] = useState({ name: '', login: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const resetForm = useCallback(() => {
    setFormData({ name: '', login: '', password: generatePassword() });
    setError(null);
  }, []);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen) return;
    const width = 560;
    if (hasMovedRef.current) return;
    const x = Math.max(8, window.innerWidth - width - 8);
    setPos({ x, y: 74 });
  }, [isOpen]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging || isPinned) return;
      const width = panelRef.current?.getBoundingClientRect().width ?? 560;
      const height = panelRef.current?.getBoundingClientRect().height ?? 400;
      const nextX = e.clientX - dragRef.current.offsetX;
      const nextY = e.clientY - dragRef.current.offsetY;
      const clampedX = Math.min(Math.max(8, nextX), window.innerWidth - width - 8);
      const clampedY = Math.min(Math.max(8, nextY), window.innerHeight - Math.min(height, window.innerHeight - 16) - 8);
      setPos({ x: clampedX, y: clampedY });
      hasMovedRef.current = true;
    };
    const onUp = () => { dragRef.current.isDragging = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPinned]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.login.trim() || !formData.password.trim()) {
      setError('Заполните все поля');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await cpApi.addMyTeamMember({ name: formData.name.trim(), login: formData.login.trim(), password: formData.password });
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Участник добавлен', type: 'success' } }));
      onSuccess();
      onClose();
    } catch (err) {
      const msg = getApiError(err);
      setError(msg.includes('уже существует') ? 'Логин уже занят' : msg);
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: msg, type: 'error' } }));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed"
      style={{ left: pos.x, top: pos.y, zIndex: 70 }}
    >
      <div
        ref={panelRef}
        className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-2xl border-2 border-blue-200 dark:border-gray-700/60 w-[560px] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto"
      >
        <div
          className={`flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700/60 select-none ${
            isPinned ? 'cursor-default' : 'cursor-move'
          }`}
          onMouseDown={(e) => {
            if (e.button !== 0 || isPinned) return;
            const rect = (panelRef.current?.getBoundingClientRect() ?? { left: pos.x, top: pos.y }) as DOMRect;
            dragRef.current.isDragging = true;
            dragRef.current.offsetX = e.clientX - rect.left;
            dragRef.current.offsetY = e.clientY - rect.top;
          }}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
              <FiUser className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Добавить участника</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Команда: {teamName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsPinned((v) => !v)}
              className="group p-2 rounded-lg bg-white/40 hover:bg-white/70 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 dark:bg-white/5 dark:hover:bg-white/10"
              title={isPinned ? 'Открепить' : 'Закрепить'}
            >
              {isPinned ? (
                <FiLock className="w-5 h-5 text-blue-700 dark:text-blue-300" />
              ) : (
                <FiMove className="w-5 h-5 text-blue-700 dark:text-blue-300" />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="group p-2 rounded-lg bg-white/40 hover:bg-white/70 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 dark:bg-white/5 dark:hover:bg-white/10"
              title="Закрыть"
            >
              <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg text-red-700 dark:text-red-200 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Имя участника *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900/40 text-gray-900 dark:text-gray-100"
              placeholder="Введите имя"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Логин *</label>
            <input
              type="text"
              value={formData.login}
              onChange={(e) => setFormData((p) => ({ ...p, login: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900/40 text-gray-900 dark:text-gray-100 font-mono"
              placeholder="Для входа в платформу"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Пароль *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                className="w-full px-4 py-2 pr-12 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900/40 text-gray-900 dark:text-gray-100 font-mono"
                placeholder="Введите пароль"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, password: generatePassword() }))}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium dark:text-blue-400"
              >
                Сгенерировать пароль
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">{formData.password.length} символов</span>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <FiPlus className="w-4 h-4" />
              {loading ? 'Добавление...' : 'Добавить участника'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
